// pages/EditLooseStone.jsx
import React, { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { FaArrowLeft } from "react-icons/fa";

import {
  useGetLooseStoneEntryByIdQuery,
  useUpdateLooseStoneEntryMutation,
} from "../toolkitServices/looseStoneApi";

import LooseStoneForm, { inflateEntryToForm } from "../components/looseStone/LooseStoneForm";
import { useGetAllStonesQuery } from "../toolkitServices/stoneApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllVendorsQuery } from "../toolkitServices/vendorApi";
import { useDispatch } from "react-redux";
import { setVendorList } from "../slices/vendorSlice";
import { setStoneList } from "../slices/stoneSlice";
import { setBranchList } from "../slices/branchSlice";
import { setStoneAttrList } from "../slices/stoneAttrSlice";
import { useGetStoneAttributesQuery } from "../toolkitServices/stoneAttributeApi";

const EditLooseStone = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: stones } = useGetAllStonesQuery();
  const { data: branches } = useGetAllBranchesQuery();
  const { data: vendors } = useGetAllVendorsQuery({ page: 1, limit: 10, search: "" });
  const { data: stnAttr } = useGetStoneAttributesQuery({}, { refetchOnMountOrArgChange: true });
  
  const { data, isFetching, isError } = useGetLooseStoneEntryByIdQuery(id, { skip: !id });
  const [updateEntry, { isLoading }] = useUpdateLooseStoneEntryMutation();

  useEffect(() => {
    if (vendors?.data?.length) dispatch(setVendorList(vendors.data));
    if (stones?.data?.length) dispatch(setStoneList(stones.data));
    if (branches?.data?.length) dispatch(setBranchList(branches.data));
    if (stnAttr?.data?.length) dispatch(setStoneAttrList(stnAttr?.data));
  }, [vendors, branches, stones, stnAttr, dispatch]);

  const existingData = useMemo(() => {
    const entry = data?.data || data;
    return entry ? inflateEntryToForm(entry) : null;
  }, [data]);

  // Form -> payload (already normalized) -> update
  const onSubmitOverride = async (payload) => {
    try {
      await updateEntry({ id, payload }).unwrap();
      toast.success("Loose stone updated");
      navigate("/stocks/loose-stones"); // <-- redirect changed as requested
    } catch (e) {
      const msg = e?.data?.msg || e?.data?.message || e?.error || "Update failed";
      toast.error(msg);
    }
  };

  const title = isFetching ? "Loading..." : isError ? "Not found" : "Edit Loose Stone";

  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-8xl mx-auto"
    >
     

      {/* Form Card */}
      <div className="bg-white shadow-lg rounded-2xl border p-3">
        {isFetching ? (
          <div className="p-4 text-sm text-slate-600">Loading…</div>
        ) : isError || !existingData ? (
          <div className="p-4 text-sm text-rose-600">Entry not found.</div>
        ) : (
          <LooseStoneForm
            existingData={existingData}
            onSubmitOverride={onSubmitOverride}
            submitLabel={isLoading ? "Saving..." : "Save Changes"}
          />
        )}
      </div>
    </motion.div>
  );
};

export default EditLooseStone;
